import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { FadeUp } from "@/components/site/Reveal";
import { ARTICLES } from "@/data/articles";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Conseils() {
  usePageMeta({
    title: "Conseils création de site internet | Les Amis du Web",
    description:
      "Nos conseils pratiques pour réussir votre site internet : guides par métier, budget, référencement. Par l'équipe Les Amis du Web, création de sites sur mesure.",
  });

  return (
    <div className="relative min-h-screen bg-[var(--bg-primary)]" data-testid="conseils-page">
      <Header />
      <main className="relative pt-40 pb-28 overflow-hidden">
        <div className="glow-orb w-[460px] h-[460px] bg-blue-600/12 -top-24 -left-32" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 relative">
          <FadeUp>
            <p className="section-label mb-5">— Conseils</p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Nos conseils pour réussir <span className="text-gradient">votre site internet.</span>
            </h1>
          </FadeUp>
          <FadeUp delay={0.16}>
            <p className="text-[var(--text-secondary)] text-base md:text-lg mt-6 leading-relaxed max-w-2xl">
              Des guides pratiques et honnêtes, écrits pour les professionnels qui se posent de vraies questions sur
              la création de leur site internet.
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 gap-6 mt-14">
            {ARTICLES.map((a, i) => (
              <motion.div
                key={a.slug}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={`/conseils/${a.slug}`}
                  className="group block rounded-2xl overflow-hidden border border-white/[0.08] bg-[#0b0c1c] h-full"
                  data-testid={`article-card-${a.slug}`}
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.imageAlt}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <span className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-[var(--accent-cyan)]">{a.category}</span>
                      <span className="flex items-center gap-1.5 text-[10px] text-[var(--text-muted)]">
                        <Clock size={11} /> {a.readTime}
                      </span>
                    </div>
                    <h2 className="font-display font-bold text-xl md:text-2xl tracking-tight group-hover:text-[var(--accent-cyan)] transition-colors">
                      {a.title}
                    </h2>
                    <p className="text-[var(--text-secondary)] text-sm mt-3 leading-relaxed">{a.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-white/90">
                      Lire l'article <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
